import React, { useEffect, useRef, useState } from 'react';
import { EventHandlingOptions, UsePrefixForGenesisMode, inventorySyncOptions, defaultSyncModes } from '../utils/common';
import { FormFieldEntry } from '../components';
import { MaxLengthValidator, RequiredValidator } from '../utils/validators';
import { useEventListener } from '../utils/customHooks';

let maxCount = 10;
let localInventorySettings = null;

const InventorySettings = ({ inventorySettings, updateTenantData }) => {
  const [count, setCount] = useState(0);

  let invDestMoveRef = useRef(null);
  let invDestDeletedRef = useRef(null);
  let invMissingAssocRef = useRef(null);
  let invPrefixMissingAssocRef = useRef(null);
  let invPrefixGenesisModeRef = useRef(null);
  let invPrefixValueRef = useRef(null);
  let invAssocCreationPrefixRef = useRef(null);
  let invAssocMissingPrefixRef = useRef(null);
  let radioGroupInvSyncLevelRef = useRef(null);
  let radioGroupDefaultSyncMode = useRef(null);
  let divRef = useRef(null);

  const handleInventorySyncLevelChange = (e) => {
    let updatedInventorySettings = { ...localInventorySettings, inventorySyncSettings: { ...localInventorySettings.inventorySyncSettings, synchronizeInventoryMode: Number(e.detail.selectedValue) } };

    updateTenantData(updatedInventorySettings);
  }

  const handleDefaultSyncModeChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, inventorySyncSettings: {
        ...localInventorySettings.inventorySyncSettings,
        inventorySyncReversed: Number(e.detail.selectedValue) === 0 ? false : true
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleDestinationMovedChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, inventorySyncSettings: {
        ...localInventorySettings.inventorySyncSettings,
        destinationMovedAuto: e.detail.selectedValue
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleDestinationDeletedChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, inventorySyncSettings: {
        ...localInventorySettings.inventorySyncSettings,
        destinationDeletedAuto: e.detail.selectedValue
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleMissingAssociationChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, inventorySyncSettings: {
        ...localInventorySettings.inventorySyncSettings,
        missingAssociationFoundAuto: e.detail.selectedValue
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handlePrefixMissingAssociationChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, inventorySyncSettings: {
        ...localInventorySettings.inventorySyncSettings,
        eligibleForUpgradeAuto: e.detail.selectedValue
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handlePrefixChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, prefixSettings: {
        ...localInventorySettings.prefixSettings,
        prefix: e.detail.sourceEvent
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleUsePrefixForCreationChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, prefixSettings: {
        ...localInventorySettings.prefixSettings,
        usePrefixForGenesis: e.detail.checked
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleUsePrefixForAssociationChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, prefixSettings: {
        ...localInventorySettings.prefixSettings,
        usePrefixForTakeover: e.detail.checked
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  const handleWhenToUsePrefixChange = (e) => {
    let updatedInventorySettings = {
      ...localInventorySettings, prefixSettings: {
        ...localInventorySettings.prefixSettings,
        usePrefixForGenesisMode: Number(e.detail.selectedValue)
      }
    };

    updateTenantData(updatedInventorySettings);
  }

  useEventListener({ eventName: 'radioSelectionChanged', handler: handleInventorySyncLevelChange, ref: radioGroupInvSyncLevelRef })
  useEventListener({ eventName: 'radioSelectionChanged', handler: handleDefaultSyncModeChange, ref: radioGroupDefaultSyncMode })
  useEventListener({ eventName: 'change', handler: handlePrefixChange, ref: invPrefixValueRef })
  useEventListener({ eventName: 'change', handler: handleUsePrefixForCreationChange, ref: invAssocCreationPrefixRef })
  useEventListener({ eventName: 'change', handler: handleUsePrefixForAssociationChange, ref: invAssocMissingPrefixRef })

  useEffect(() => {
    window.scrollTo(0, 0);
    divRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    localInventorySettings = { ...inventorySettings };
  }, [inventorySettings])

  useEffect(() => {
    if (count === maxCount) {
      return;
    }

    let localInvDestMoveRef = null;
    let localInvDestDeletedRef = null;
    let localInvMissingAssocRef = null;
    let localInvPrefixMissingAssocRef = null;
    let localInvPrefixGenesisModeRef = null;

    if (invDestMoveRef.current.childNodes[0].dropdownMenu) {
      localInvDestMoveRef = invDestMoveRef.current
      localInvDestDeletedRef = invDestDeletedRef.current
      localInvMissingAssocRef = invMissingAssocRef.current
      localInvPrefixMissingAssocRef = invPrefixMissingAssocRef.current
      localInvPrefixGenesisModeRef = invPrefixGenesisModeRef.current

      invDestMoveRef.current.childNodes[0].dropdownMenu.canDelete = false;
      invDestDeletedRef.current.childNodes[0].dropdownMenu.canDelete = false;
      invMissingAssocRef.current.childNodes[0].dropdownMenu.canDelete = false;
      invPrefixMissingAssocRef.current.childNodes[0].dropdownMenu.canDelete = false;
      invPrefixGenesisModeRef.current.childNodes[0].dropdownMenu.canDelete = false;
    }
    if (invDestMoveRef.current.childNodes[0].selectList) {
      invDestMoveRef.current.childNodes[0].selectList.addEventListener('selectionChange', handleDestinationMovedChange);
      invDestDeletedRef.current.childNodes[0].selectList.addEventListener('selectionChange', handleDestinationDeletedChange);
      invMissingAssocRef.current.childNodes[0].selectList.addEventListener('selectionChange', handleMissingAssociationChange);
      invPrefixMissingAssocRef.current.childNodes[0].selectList.addEventListener('selectionChange', handlePrefixMissingAssociationChange);
      invPrefixGenesisModeRef.current.childNodes[0].selectList.addEventListener('selectionChange', handleWhenToUsePrefixChange);
    } else {
      setCount(prev => prev + 1);
    }

    return (() => {
      if (localInvDestMoveRef) {
        localInvDestMoveRef.removeEventListener('selectionChange', handleDestinationMovedChange);
        localInvDestDeletedRef.removeEventListener('selectionChange', handleDestinationDeletedChange);
        localInvMissingAssocRef.removeEventListener('selectionChange', handleMissingAssociationChange);
        localInvPrefixMissingAssocRef.removeEventListener('selectionChange', handlePrefixMissingAssociationChange);
        localInvPrefixGenesisModeRef.removeEventListener('selectionChange', handleWhenToUsePrefixChange);
      }
    })
  }, [count])

  useEffect(() => {
    invDestMoveRef.current.validators = [RequiredValidator('Required field')];
    invDestDeletedRef.current.validators = [RequiredValidator('Required field')];
    invMissingAssocRef.current.validators = [RequiredValidator('Required field')];
    invPrefixMissingAssocRef.current.validators = [RequiredValidator('Required field')];
    invPrefixGenesisModeRef.current.validators = [RequiredValidator('Required field')];
    invPrefixValueRef.current.validators = [MaxLengthValidator(12, 'Maximum length permitted is 12 characters')];

    radioGroupInvSyncLevelRef.current.selectedOption = JSON.stringify(inventorySettings.inventorySyncSettings.synchronizeInventoryMode);
    radioGroupDefaultSyncMode.current.selectedOption = JSON.stringify(inventorySettings.inventorySyncSettings.inventorySyncReversed ? 1 : 0);

    disableInitialElements(inventorySettings.prefixSettings.prefix)
  }, [inventorySettings]);

  const disableInitialElements = (prefixValue) => {
    if (!prefixValue || prefixValue === '') {
      invAssocMissingPrefixRef.current.disabled = true;
      invAssocMissingPrefixRef.current.checked = false;
      invAssocCreationPrefixRef.current.disabled = true;
      invAssocCreationPrefixRef.current.checked = false;
      invPrefixGenesisModeRef.current.disabled = true;
      return;
    }
  }

  return (
    <div className='flex flex-col ml-8 mt-8 p-2' ref={divRef}>
      <div className='mb-2'>
        <gz-title fontSize="18" fontWeight="500">Inventory Synchronization</gz-title>
      </div>
      <div className='mb-2'>
        <gz-text fontSize="12">
          Use the options on this page to configure sync tasks for your Kaseya inventory.
        </gz-text>
      </div>
      <div className='mb-3'>
        <gz-text fontSize="12">
          The Bitdefender plugin will update the GravityZone inventory to match any changes found.
        </gz-text>
      </div>
      <div>
        <div className='mt-6 mb-2'>
          <gz-title fontSize="18" fontWeight="500">Synchronization level</gz-title>
        </div>
        <FormFieldEntry
          ref={radioGroupInvSyncLevelRef}
          formFieldId="radio_group_inv_level"
          typeOfEnclosing="gz-radio-group"
          value={inventorySyncOptions}
        />
        <div className='mt-6 mb-2'>
          <gz-title fontSize="18" fontWeight="500">Default synchronization mode</gz-title>
        </div>
        <div className='mb-2'>
          <gz-text fontSize="12">
            Select the default mode for inventory synchronization. The inclusion or exclusion of individual organizations and groups is independent of the inventory synchronization mode.
          </gz-text>
        </div>
        <FormFieldEntry
          ref={radioGroupDefaultSyncMode}
          formFieldId="radio_group_def_sync_mode"
          typeOfEnclosing="gz-radio-group"
          value={defaultSyncModes}
        />
        <div className='mt-6 mb-2'>
          <gz-title fontSize="18" fontWeight="500">Synchronization event handling</gz-title>
        </div>
        <div className='mb-2'>
          <gz-text fontSize="12">
            Select event handling settings during synchronization.
          </gz-text>
        </div>
        <FormFieldEntry
          ref={invDestMoveRef}
          label="Destination moved"
          formFieldId="inv_dest_moved"
          typeOfEnclosing="gz-single-select"
          placeholder="Select value"
          filterOptions={EventHandlingOptions}
          value={inventorySettings.inventorySyncSettings.destinationMovedAuto}
        />
        <FormFieldEntry
          ref={invDestDeletedRef}
          label="Destination deleted"
          formFieldId="inv_dest_deleted"
          typeOfEnclosing="gz-single-select"
          placeholder="Select value"
          filterOptions={EventHandlingOptions}
          value={inventorySettings.inventorySyncSettings.destinationDeletedAuto}
        />
        <FormFieldEntry
          ref={invMissingAssocRef}
          label="Association missing"
          formFieldId="inv_missing_association"
          typeOfEnclosing="gz-single-select"
          placeholder="Select value"
          filterOptions={EventHandlingOptions}
          value={inventorySettings.inventorySyncSettings.missingAssociationFoundAuto}
        />
        <FormFieldEntry
          ref={invPrefixMissingAssocRef}
          label="Prefix-based association missing"
          formFieldId="inv_prefix_missing_association"
          typeOfEnclosing="gz-single-select"
          placeholder="Select value"
          filterOptions={EventHandlingOptions}
          value={inventorySettings.inventorySyncSettings.eligibleForUpgradeAuto}
        />
        <div className='mt-8 mb-2'>
          <gz-title fontSize="18" fontWeight="500">Prefix settings</gz-title>
        </div>
        <div className='mb-4'>
          <gz-text fontSize="12">
            These settings are used when creating or associating organizations. Specify a prefix to enable options.
          </gz-text>
        </div>
      </div>
      <div className='mb-16'>
        <FormFieldEntry
          ref={invPrefixValueRef}
          label="Prefix (used before company name)"
          formFieldId="inv_prefix_value"
          typeOfEnclosing="gz-input"
          placeholder="Enter prefix"
          value={inventorySettings.prefixSettings.prefix}
        />

        <div className='d-flex flex-row'>
          <div className='me-5'>
            <FormFieldEntry
              ref={invAssocCreationPrefixRef}
              label="Use prefix for creation"
              formFieldId="associationCreationPrefix"
              typeOfEnclosing="gz-checkbox"
              value={inventorySettings.prefixSettings.usePrefixForGenesis}
              isChecked={inventorySettings.prefixSettings.usePrefixForGenesis}
              disableCondition={inventorySettings.prefixSettings.prefix === null ||
                inventorySettings.prefixSettings.prefix === ''}
            />
          </div>

          <div style={{ marginLeft: '2rem' }}>
            <FormFieldEntry
              ref={invPrefixGenesisModeRef}
              label=""
              formFieldId="inv_pref_add_choice"
              typeOfEnclosing="gz-single-select"
              placeholder="Select value"
              filterOptions={UsePrefixForGenesisMode}
              value={inventorySettings.prefixSettings.usePrefixForGenesisMode}
              disableCondition={!inventorySettings.prefixSettings.usePrefixForGenesis ||
                (inventorySettings.prefixSettings.prefix === null ||
                  inventorySettings.prefixSettings.prefix === '')}
            />
          </div>
        </div>
        <FormFieldEntry
          ref={invAssocMissingPrefixRef}
          label="Use prefix for association"
          formFieldId="associationMissingPrefix"
          typeOfEnclosing="gz-checkbox"
          value={inventorySettings.prefixSettings.usePrefixForTakeover}
          isChecked={inventorySettings.prefixSettings.usePrefixForTakeover}
          disableCondition={inventorySettings.prefixSettings.prefix === null ||
              inventorySettings.prefixSettings.prefix === ''}
        />
      </div>
    </div>
  )
}

export default InventorySettings